import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { setLanguageCode } from '@/i18n';
import { cn } from '@/lib/utils';
import { ES, GB } from 'country-flag-icons/react/3x2';
import {
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Lock,
  LogOut,
  Moon,
  Sun,
  User,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import config from 'src/config';
import { i18n } from 'src/i18n';
import authActions from 'src/modules/auth/authActions';
import authSelectors from 'src/modules/auth/authSelectors';
import layoutActions from 'src/modules/layout/layoutActions';
import layoutSelectors from 'src/modules/layout/layoutSelectors';
import { getHistory } from 'src/modules/store';

interface HeaderProps {
  className?: string;
  showCollapse?: boolean;
}

const Header = ({
  className,
  showCollapse = true,
}: HeaderProps) => {
  const dispatch = useDispatch();
  const { theme, setTheme } = useTheme();
  const [currentLang, setCurrentLang] = useState(
    localStorage.getItem('language') || 'es-ES',
  );

  const menuVisible = useSelector(
    layoutSelectors.selectMenuVisible,
  );
  const currentTenant = useSelector(
    authSelectors.selectCurrentTenant,
  );
  const _currentUser = useSelector(
    authSelectors.selectCurrentUser,
  );
  const userDropdownText = useSelector(
    authSelectors.selectCurrentUserNameOrEmailPrefix,
  );
  const userDropdownAvatar = useSelector(
    authSelectors.selectCurrentUserAvatar,
  );

  const languages = [
    { code: 'en', label: 'English', flag: GB },
    { code: 'es-ES', label: 'Español', flag: ES },
  ];

  const currentLanguage =
    languages.find((lang) => lang.code === currentLang) ||
    languages[0];

  const handleLanguageChange = async (langCode: string) => {
    try {
      setLanguageCode(langCode);
      setCurrentLang(langCode);
      window.location.reload();
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  const doSignout = () => {
    dispatch(authActions.doSignout());
  };

  const doNavigateToProfile = () => {
    getHistory().push('/profile');
  };

  const doNavigateToPasswordChange = () => {
    getHistory().push('/password-change');
  };

  const doNavigateToTenants = () => {
    getHistory().push('/tenant');
  };

  const doToggleMenu = () => {
    dispatch(layoutActions.doToggleMenu());
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-50 w-full border-b',
        'bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60',
        className,
      )}
    >
      <div className="flex h-14 items-center px-6">
        <div className="flex items-center gap-4 flex-1">
          {showCollapse && (
            <Button
              variant="ghost"
              size="icon"
              onClick={doToggleMenu}
              className="shrink-0"
            >
              {menuVisible ? (
                <ChevronLeft className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
              <span className="sr-only">Toggle menu</span>
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-accent relative"
              >
                <currentLanguage.flag className="w-4 h-4 rounded-sm" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-[150px]"
            >
              {languages.map((lang) => {
                const Flag = lang.flag;
                return (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() =>
                      handleLanguageChange(lang.code)
                    }
                    className={cn(
                      'cursor-pointer flex items-center gap-2',
                      currentLang === lang.code &&
                        'bg-accent',
                    )}
                  >
                    <Flag className="w-4 h-4 rounded-sm" />
                    <span>{lang.label}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 hover:bg-accent"
            onClick={() =>
              setTheme(theme === 'light' ? 'dark' : 'light')
            }
          >
            <Sun className="h-4 w-4 rotate-0 scale-100 transition-transform dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-transform dark:rotate-0 dark:scale-100" />
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="relative h-8 w-8 rounded-full"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src={userDropdownAvatar || undefined}
                    alt={userDropdownText}
                  />
                  <AvatarFallback>
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-56"
              align="end"
              forceMount
            >
              <div className="flex items-center justify-start gap-2 p-2">
                <div className="flex flex-col space-y-1 leading-none">
                  <p className="font-medium">
                    {userDropdownText}
                  </p>
                  {currentTenant?.name && (
                    <p className="w-[200px] truncate text-sm text-muted-foreground">
                      {currentTenant.name}
                    </p>
                  )}
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={doNavigateToProfile}
              >
                <User className="mr-2 h-4 w-4" />
                <span>{i18n('common.profile')}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={doNavigateToPasswordChange}
              >
                <Lock className="mr-2 h-4 w-4" />
                <span>
                  {i18n('auth.passwordChange.title')}
                </span>
              </DropdownMenuItem>
              {['multi', 'multi-with-subdomain'].includes(
                config.tenantMode,
              ) && (
                <DropdownMenuItem
                  onClick={doNavigateToTenants}
                >
                  <LayoutGrid className="mr-2 h-4 w-4" />
                  <span>{i18n('auth.tenants')}</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doSignout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>{i18n('auth.signout')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;
